import { Box, Flex, VStack, Textarea } from '@chakra-ui/react'
import { LuCheck as Check, LuX as X } from 'react-icons/lu'
import { Text, useT } from '@minetrace/ui'
import { useFormColors } from '@minetrace/ui/forms'

export interface SurveyQuestionItem {
  questionId: number
  text: string
  isCritical: boolean
  answer: boolean | null
  comments: string | null
}

interface SustainabilitySurveyProps {
  questions: SurveyQuestionItem[]
  readOnly?: boolean
  onAnswer?: (questionId: number, answer: boolean | null) => void
  onComment?: (questionId: number, comments: string) => void
}

const GREEN = '#1d6664'
const RED = '#b23a26'

/**
 * Cuestionario de sostenibilidad con toggle Sí/No segmentado bien visible y
 * campo de comentario evidente. Sirve editable (registro) y de solo lectura
 * (evaluación). Sigue las reglas de Forms del skill UI/UX: labels visibles,
 * helper text persistente, estados claros y touch targets cómodos.
 */
export default function SustainabilitySurvey({ questions, readOnly, onAnswer, onComment }: SustainabilitySurveyProps) {
  const c = useFormColors()
  const { t } = useT()

  if (questions.length === 0) {
    return (
      <Box p={6} textAlign="center" bg={c.bgHover} borderRadius="xl">
        <Text fontSize="sm" color={c.textMuted}>{t('transportistas.nuevo.encuesta.empty')}</Text>
      </Box>
    )
  }

  const renderToggle = (q: SurveyQuestionItem) => {
    // Solo lectura: muestra la respuesta como badge de color.
    if (readOnly) {
      if (q.answer === null) {
        return (
          <Box px={3} py={1.5} borderRadius="lg" bg={c.bgInput} color={c.textMuted} fontSize="sm" fontWeight="600" flexShrink={0}>
            {t('transportistas.nuevo.encuesta.unanswered')}
          </Box>
        )
      }
      const ok = q.answer
      return (
        <Flex alignItems="center" gap={1.5} px={3} py={1.5} borderRadius="lg" flexShrink={0}
          bg={ok ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.12)'} color={ok ? GREEN : RED} fontWeight="700" fontSize="sm">
          {ok ? <Check size={15} /> : <X size={15} />}
          {ok ? t('common.yes') : t('common.no')}
        </Flex>
      )
    }

    // Editable: toggle segmentado Sí / No.
    const segBtn = (value: boolean) => {
      const active = q.answer === value
      const color = value ? GREEN : RED
      return (
        <Box
          as="button"
          type="button"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          gap={1.5}
          minW="64px"
          h="40px"
          px={4}
          borderRadius="md"
          fontSize="sm"
          fontWeight="700"
          bg={active ? color : 'transparent'}
          color={active ? 'white' : c.textMuted}
          cursor="pointer"
          transition="all 120ms ease"
          _hover={active ? {} : { color: c.text, bg: c.bgHover }}
          onClick={() => onAnswer?.(q.questionId, active ? null : value)}
        >
          {value ? <Check size={15} /> : <X size={15} />}
          {value ? t('common.yes') : t('common.no')}
        </Box>
      )
    }
    return (
      <Flex bg={c.bgInput} p={1} borderRadius="lg" gap={1} flexShrink={0}>
        {segBtn(true)}
        {segBtn(false)}
      </Flex>
    )
  }

  return (
    <VStack gap={3} alignItems="stretch">
      {questions.map((q) => (
        <Box key={q.questionId} p={4} bg={c.bgCard} borderRadius="xl" border="1px solid" borderColor={c.border}>
          <Flex alignItems="flex-start" justifyContent="space-between" gap={4}>
            <Text fontSize="sm" fontWeight="500" color={c.text} lineHeight="snug">
              {q.text}
              {q.isCritical && <Text as="span" color={RED}> *</Text>}
            </Text>
            {renderToggle(q)}
          </Flex>

          {/* Comentario */}
          {readOnly ? (
            q.comments ? (
              <Box mt={3}>
                <Text fontSize="xs" fontWeight="600" color={c.textMuted} mb={1}>{t('transportistas.nuevo.encuesta.commentLabel')}</Text>
                <Text fontSize="sm" color={c.text}>{q.comments}</Text>
              </Box>
            ) : null
          ) : (
            <Box mt={3}>
              <Text fontSize="xs" fontWeight="600" color={c.textMuted} mb={1}>{t('transportistas.nuevo.encuesta.commentLabel')}</Text>
              <Textarea
                value={q.comments ?? ''}
                onChange={(e) => onComment?.(q.questionId, e.target.value)}
                placeholder={t('transportistas.nuevo.encuesta.commentPlaceholder')}
                rows={2}
                fontSize="sm"
                bg={c.bgInput}
                border="1px solid"
                borderColor={c.border}
                borderRadius="lg"
                color={c.text}
                _placeholder={{ color: c.textMuted }}
                _focus={{ outline: 'none', borderColor: c.accent, boxShadow: `0 0 0 1px ${c.accent}` }}
              />
            </Box>
          )}
        </Box>
      ))}
    </VStack>
  )
}
